import { X, Navigation, Users, Maximize2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useWardStore } from '@/stores/wardStore'
import { useUIStore } from '@/stores/uiStore'
import { useDirectionsStore } from '@/stores/directionsStore'
import { VI_MESSAGES } from '@/constants/vietnamese'
import { formatNumber } from '@/utils/vietnamese-format'
import type { WardInfo } from '@/types/ward'
import { motion, AnimatePresence } from 'framer-motion'
import { navigationService } from '@/services/NavigationService'

interface WardInfoPopupProps {
  ward: WardInfo
}

export function WardInfoPopup({ ward }: WardInfoPopupProps) {
  const selectWard = useWardStore((state) => state.selectWard)
  const setActiveTab = useUIStore((state) => state.setActiveTab)
  const setDestination = useDirectionsStore((state) => state.setDestination)

  const handleClose = () => {
    selectWard(null)
  }

  const handleDirections = async () => {
    // Navigate directly using NavigationService
    try {
      await navigationService.navigateFromCurrentLocation(ward.xaphuong)
    } catch {
      // Fallback to directions tab
      setDestination(ward.xaphuong)
      setActiveTab('directions')
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed right-4 bottom-4 z-30 w-80"
      >
        <Card className="shadow-2xl bg-white">
          {/* Header */}
          <div className="flex items-start justify-between p-4 pb-0">
            <div className="flex-1">
              <h3 className="text-xl font-bold">{ward.xaphuong}</h3>
              {/* <p className="text-sm text-gray-600">{ward.xaphuong}, Hà Nội</p> */}
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Separator className="my-4" />

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-4 px-4">
            {/* Dân số */}
            {ward.danso && (
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.LABELS.POPULATION}</p>
                  <p className="text-lg font-semibold">{formatNumber(ward.danso)}</p>
                  <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.UNITS.POPULATION}</p>
                </div>
              </div>
            )}

            {/* Diện tích */}
            {ward.dientichpl && (
              <div className="flex items-start gap-3">
                <Maximize2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-xs text-gray-500">{VI_MESSAGES.WARD.LABELS.AREA}</p>
                  <p className="text-lg font-semibold">{formatNumber(ward.dientichpl, 2)}</p>
                  <p className="text-xs text-gray-500">km²</p>
                </div>
              </div>
            )}
          </div>

          {/* Chairman Info */}
          {ward.chutich && (
            <>
              <Separator className="my-4" />
              <div className="px-4">
                <p className="text-sm font-medium text-gray-700">
                  {VI_MESSAGES.WARD.LABELS.CHAIRMAN}
                </p>
                <p className="text-base font-semibold">{ward.chutich}</p>
              </div>
            </>
          )}

          {/* BITHU Info */}
          {ward.bithu && (
            <div className="px-4 mt-3">
              <p className="text-sm font-medium text-gray-700">
                {VI_MESSAGES.WARD.LABELS.BITHU}
              </p>
              <p className="text-base font-semibold">{ward.bithu}</p>
            </div>
          )}

          {/* MATTRAN Info */}
          {ward.mattran && (
            <div className="px-4 mt-3">
              <p className="text-sm font-medium text-gray-700">
                {VI_MESSAGES.WARD.LABELS.MTTQ}
              </p>
              <p className="text-base font-semibold">{ward.mattran}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 p-4 pt-0">
            <Button onClick={handleDirections} className="w-full" size="sm">
              <Navigation className="mr-2 h-4 w-4" />
              {VI_MESSAGES.WARD.ACTIONS.GET_DIRECTIONS}
            </Button>
          </div>
        </Card>
      </motion.div>
    </AnimatePresence>
  )
}

